'use client';

import type { HTMLAttributes, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { useTrackImpression } from '../../../common/hooks/useTrackImpression';
import { useProductLinkNavigation } from '../../hooks';
import { ProductImageWrapper } from '../ProductImageWrapper';
import type { BaseProductImageItem } from './BaseProductImageItem';

export interface ProductShellProps<T extends BaseProductImageItem>
  extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  item: T;
  checked?: boolean;
  index?: number;
  children?: ReactNode;
  imageClassName?: string;
  /** 상품 정보 영역 슬롯 */
  infoBlock?: ReactNode;
  onClickCheckbox?: () => void;
  onClickCloseButton?: () => void;
  onClickLikeButton?: (item: T, nextIsLiked: boolean) => void;
  /** 29cm 딥링크 활성화 여부 */
  enableDeeplink?: boolean;
  /** navigate 대상 URL — 변형별 wrapper에서 해당 변형의 URL 필드를 넘김 */
  productUrl?: string;
  callbackClick?: (item: T) => void;
  callbackImpression?: (item: T) => void;
}

export const ProductShell = <T extends BaseProductImageItem>({
  className,
  item,
  checked,
  index,
  children,
  imageClassName,
  infoBlock,
  onClickCheckbox,
  onClickCloseButton,
  onClickLikeButton,
  enableDeeplink = false,
  productUrl,
  callbackClick,
  callbackImpression,
  ...rest
}: ProductShellProps<T>) => {
  const navigateToProduct = useProductLinkNavigation();
  const { ref } = useTrackImpression<HTMLDivElement>({
    onImpression: () => {
      if (callbackImpression) callbackImpression(item);
    },
  });

  const onClickProduct = () => {
    if (enableDeeplink) {
      if (productUrl) {
        navigateToProduct(productUrl);
      }
      callbackClick?.(item);
      return;
    }

    // TODO: 추후 PDP 이동으로 변경 필요.
    onClickCheckbox?.();
    callbackClick?.(item);
  };

  const handleLikeButton = (_goodsNo: string | number, nextIsLiked: boolean) => {
    onClickLikeButton?.(item, nextIsLiked);
  };

  return (
    <div
      ref={ref}
      role="button"
      className={twMerge(
        'cursor-pointer relative w-full h-full overflow-hidden flex flex-col',
        'bg-neutral-color-white',
        className,
      )}
      tabIndex={0}
      {...rest}
    >
      <ProductImageWrapper
        className={imageClassName}
        item={item}
        checked={checked}
        index={index}
        onClickProduct={onClickProduct}
        onClickCheckbox={onClickCheckbox}
        onClickCloseButton={onClickCloseButton}
        onClickLikeButton={handleLikeButton}
      />
      {infoBlock && <div className="flex-1">{infoBlock}</div>}

      {children && <div className="mt-auto">{children}</div>}
    </div>
  );
};
